import type { PropertiesHyphen } from "csstype"

export function createTheme<Def extends CreateThemeDefinition>(def: Def) {
  const theme: Record<string, Record<string, string>> = {}
  const allVars: Record<string, Record<string, string | SchemeValue>> = {}
  const allLiterals: Record<string, Record<string | number, string>> = {}

  Object.entries(def).forEach(([themeKey, { varPrefix, literals, vars }]) => {
    const themeValue: Record<string, string> = {}
    theme[themeKey] = themeValue

    const varsValue: Record<string, string | SchemeValue> = {}
    allVars[themeKey] = varsValue

    if (literals) {
      Object.assign(themeValue, literals)
      allLiterals[themeKey] = literals
    }

    if (vars)
      Object.entries(vars).forEach(([variable, value]) => {
        if (isSchemeValue(value)) {
          const name = getUniqueVarName({
            name: variable,
            prefix: varPrefix,
          })
          const lightName = getUniqueVarName({
            name: variable,
            prefix: varPrefix,
            scheme: "light",
          })
          const darkName = getUniqueVarName({
            name: variable,
            prefix: varPrefix,
            scheme: "dark",
          })

          themeValue[name] = toCSSVar(name)
          varsValue[lightName] = value.light
          varsValue[darkName] = value.dark
          varsValue[name] = value
        } else {
          const name = getUniqueVarName({ name: variable, prefix: varPrefix })
          themeValue[variable] = toCSSVar(name)
          varsValue[name] = value
        }
      })
  })

  // Casting because there's just no way to convince TS that the implementation is matching the result type.
  // Tests are here to validate that it is indeed the case.
  return {
    theme,
    vars: allVars,
    literals: allLiterals,
  } as unknown as ThemeResult<Def>
}

export function isSchemeValue(value: unknown): value is SchemeValue {
  return Boolean(
    value && typeof value === "object" && "light" in value && "dark" in value,
  )
}

export function createCSSTheme<
  V extends Record<string, Vars>,
  L extends Record<string, Literals>,
  Def extends Record<keyof V & keyof L, (keyof PropertiesHyphen)[]>,
>(vars: V, literals: L, def: Def) {
  return Object.entries(def).reduce(
    (acc, entry) => {
      const [propName, cssKeys] = entry

      const propLiterals = Object.values(literals[propName] ?? {})
      const propVars = Object.keys(vars[propName] ?? {}).map(toCSSVar)

      // CSS variables can be useful for calculations;
      // trust the dev they're using correct values in their variables.
      const acceptVariables = new Set(["borderRadius", "borderWidth"]).has(
        propName,
      )

      const cssValues = [
        ...propLiterals,
        ...propVars,
        ...(acceptVariables ? [/var\(--\w+\)/] : []),
      ]

      cssKeys.forEach((cssKey) => {
        acc[cssKey] = cssValues
      })

      return acc
    },
    {} as Record<string, (string | RegExp)[]>,
  )
}

function getUniqueVarName(params: {
  name: string
  prefix?: string
  scheme?: ColorScheme
}) {
  const { name, prefix, scheme } = params

  const prefixPart = prefix ? `${prefix}-` : ""
  const schemePart = scheme ? `${scheme}-` : ""

  return `${prefixPart}${schemePart}${camelToDashed(name)}`
}

function camelToDashed(str: string) {
  return str.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)
}

function toCSSVar<Name extends string>(name: Name): string {
  return `var(--${name})`
}

type CreateThemeDefinition = Record<string, ThemeValue>

type ThemeValue =
  | {
      literals?: Literals
      varPrefix?: string
      vars: Vars
    }
  | {
      literals: Literals
      varPrefix?: never
      vars?: never
    }

export type ColorScheme = "light" | "dark"
type SchemeValue = { light: string; dark: string }

type Literals = Record<string | number, string>
type Vars = Record<string, string | SchemeValue>

type ThemeResult<Def extends CreateThemeDefinition> = {
  theme: { [Key in keyof Def]: ThemeResultValue<Def[Key]> }
  vars: { [Key in keyof Def]: Vars }
  literals: { [Key in keyof Def]: Literals }
}

type ThemeResultValue<Value extends ThemeValue> = Value["literals"] & {
  [Key in keyof Value["vars"]]: string
}
